import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Power, PowerOff, Trash2, Copy, CalendarOff, X, Trash, Package, Pencil } from 'lucide-react';
import { fmtDate } from '../utils/dates';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { SportIcon, SPORT_COLORS } from '../components/SportIcon';
import { fmtHours, ymd } from '../utils/sportsUtils';

const SPORT_CONFIG = {
  pickleball:  { color: SPORT_COLORS.pickleball },
  tennis:      { color: SPORT_COLORS.tennis },
  badminton:   { color: SPORT_COLORS.badminton },
  basketball:  { color: SPORT_COLORS.basketball },
  volleyball:  { color: SPORT_COLORS.volleyball },
  futsal:      { color: SPORT_COLORS.futsal },
  other:       { color: SPORT_COLORS.other },
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function BlockDatesModal({ court, onClose, onRefresh }) {
  const [blockedDates, setBlockedDates] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchBlocked = () => {
    sportsApi.get(`/sports/courts/${court.id}/blocked-dates`)
      .then(r => setBlockedDates(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBlocked(); }, [court.id]);

  const handleBlock = async () => {
    if (!newDate) { toast.error('Pick a date first'); return; }
    setSaving(true);
    try {
      await sportsApi.post(`/sports/courts/${court.id}/block-date`, { date: newDate, reason: newReason });
      toast.success(`${fmtDate(newDate)} blocked`);
      setNewDate(''); setNewReason('');
      fetchBlocked();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to block date'); }
    finally { setSaving(false); }
  };

  const handleUnblock = async (date) => {
    try {
      await sportsApi.delete(`/sports/courts/${court.id}/block-date`, { params: { date } });
      toast.success(`${fmtDate(date)} unblocked`);
      fetchBlocked();
    } catch { toast.error('Failed to unblock'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}>
      <div className="rounded-xl w-full max-w-md" style={{ background: '#141414', border: '1px solid #2A2A2A' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
          <div>
            <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Block Dates</p>
            <p className="text-xs mt-0.5" style={{ color: '#737373' }}>{court.name} — no bookings on blocked dates</p>
          </div>
          <button onClick={onClose} style={{ color: '#737373', fontSize: 20, lineHeight: 1 }}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* Add block form */}
          <div className="rounded-lg p-4" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
            <p className="text-xs uppercase tracking-wide mb-3" style={{ color: '#737373' }}>Block a Date</p>
            <div className="flex gap-2 mb-2">
              <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                min={ymd()}
                data-testid="sports-block-date-input"
                style={{ flex: 1, background: '#141414', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', outline: 'none', fontSize: 13 }} />
            </div>
            <input value={newReason} onChange={e => setNewReason(e.target.value)}
              placeholder="Reason (e.g. Tournament day)"
              data-testid="sports-block-reason-input"
              style={{ width: '100%', background: '#141414', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', outline: 'none', fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }} />
            <button onClick={handleBlock} disabled={saving || !newDate}
              data-testid="sports-block-date-btn"
              className="w-full py-2 rounded-md text-sm font-bold uppercase transition-colors"
              style={{ background: !newDate ? '#2A2A2A' : '#E63946', color: !newDate ? '#555' : '#fff', cursor: !newDate ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Block This Date'}
            </button>
          </div>

          {/* Blocked list */}
          {loading ? (
            <div className="text-center py-4 text-sm" style={{ color: '#555' }}>Loading…</div>
          ) : blockedDates.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: '#555' }}>No blocked dates.</p>
          ) : (
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#737373' }}>Currently Blocked</p>
              <div className="space-y-2">
                {blockedDates.map(b => (
                  <div key={b.date} data-testid={`sports-blocked-date-${b.date}`}
                    className="flex items-center justify-between rounded-lg px-3 py-2.5"
                    style={{ background: '#FF2D5510', border: '1px solid #FF2D5530' }}>
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#FF8080' }}>{fmtDate(b.date)}</p>
                      {b.reason && <p className="text-xs mt-0.5" style={{ color: '#737373' }}>{b.reason}</p>}
                    </div>
                    <button onClick={() => handleUnblock(b.date)}
                      data-testid={`sports-unblock-date-${b.date}`}
                      className="p-1.5 rounded-md transition-colors"
                      style={{ background: '#FF2D5520', color: '#FF2D55' }}>
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CourtCard({ court, onEdit, onToggle, onDelete, onDuplicate, onBlockDates }) {
  const sport = SPORT_CONFIG[court.sport] || SPORT_CONFIG.other;
  const days = (court.open_days || []).map(d => DAYS[d]).join(', ') || 'No days set';
  // Day-band schedules: rate range spans all schedules' bands; hours vary by day.
  const hasGroups = court.schedules?.length > 0;
  const allBands = [...(court.rate_bands || []), ...((court.schedules || []).flatMap(s => s.rate_bands || []))];
  const rate = court.rate_type === 'flat' && !hasGroups
    ? `₱${court.flat_rate?.toLocaleString()}/hr`
    : allBands.length > 0
      ? `₱${Math.min(...allBands.map(b => b.price)).toLocaleString()}–₱${Math.max(...allBands.map(b => b.price)).toLocaleString()}/hr`
      : 'Rate TBD';
  const blockedCount = court.blocked_dates?.length || 0;

  return (
    <div data-testid={`sports-court-card-${court.id}`}
      className="rounded-xl overflow-hidden transition-all"
      style={{ background: '#141414', border: `1px solid ${court.active ? '#2A2A2A' : '#1A1A1A'}`, opacity: court.active ? 1 : 0.6 }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-lg"
              style={{ background: sport.color + '18' }}>
              <SportIcon sport={court.sport} size={22} color={sport.color} />
            </span>
            <div>
              <h3 className="font-bold text-base" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {court.name}
              </h3>
              <p className="text-xs capitalize mt-0.5" style={{ color: sport.color }}>{court.sport}</p>
            </div>
          </div>
          {!court.active && (
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#2A2A2A', color: '#737373' }}>Inactive</span>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {[
            { label: 'Hours', value: hasGroups ? 'Varies by day' : fmtHours(court.open_time, court.close_time) },
            { label: 'Rate', value: rate },
            { label: 'Slot', value: `${court.slot_minutes || 30} min` },
            { label: 'Open', value: days },
          ].map(d => (
            <div key={d.label}>
              <p className="text-xs" style={{ color: '#737373' }}>{d.label}</p>
              <p className="text-sm font-medium mt-0.5" style={{ color: '#ccc' }}>{d.value}</p>
            </div>
          ))}
        </div>
        {blockedCount > 0 && (
          <div className="mt-3 rounded-md px-3 py-1.5 flex items-center gap-1.5" style={{ background: '#FF2D5510', border: '1px solid #FF2D5530' }}>
            <CalendarOff className="h-3 w-3" style={{ color: '#FF2D55' }} />
            <span className="text-xs" style={{ color: '#FF8080' }}>{blockedCount} date{blockedCount > 1 ? 's' : ''} blocked</span>
          </div>
        )}
      </div>
      <div className="flex items-center border-t px-5 py-3 gap-2 flex-wrap" style={{ borderColor: '#1E1E1E' }}>
        <button data-testid={`sports-court-edit-${court.id}`}
          onClick={() => onEdit(court)}
          className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors"
          style={{ background: '#2A2A2A', color: '#A3A3A3', flex: '1 1 auto' }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = '#A3A3A3'}>
          <Edit2 className="h-3.5 w-3.5" /> Edit
        </button>
        <button data-testid={`sports-court-duplicate-${court.id}`}
          onClick={() => onDuplicate(court)}
          title="Duplicate this court"
          className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors"
          style={{ background: '#2A2A2A', color: '#A3A3A3', flex: '1 1 auto' }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = '#A3A3A3'}>
          <Copy className="h-3.5 w-3.5" /> Copy
        </button>
        <button data-testid={`sports-court-block-dates-${court.id}`}
          onClick={() => onBlockDates(court)}
          title="Block specific dates"
          className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors"
          style={{ background: '#2A2A2A', color: '#A3A3A3', flex: '1 1 auto' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#FF2D55'; e.currentTarget.style.background = '#FF2D5518'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#A3A3A3'; e.currentTarget.style.background = '#2A2A2A'; }}>
          <CalendarOff className="h-3.5 w-3.5" /> Block Date
        </button>
        <button data-testid={`sports-court-toggle-${court.id}`}
          onClick={() => onToggle(court)}
          className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors"
          style={{ background: court.active ? '#1A2A1A' : '#2A1A1A', color: court.active ? '#00E676' : '#737373' }}>
          {court.active ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
        </button>
        <button data-testid={`sports-court-delete-${court.id}`}
          onClick={() => onDelete(court)}
          className="p-2 rounded-md text-xs transition-colors"
          style={{ background: '#2A1A1A', color: '#FF2D55' }}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

const UNIT_OPTIONS = ['piece', 'pair', 'set', 'racket', 'ball', 'shuttle', 'net', 'glove'];
const PRICE_TYPE_OPTIONS = [
  { value: 'per_session', label: 'Flat / Session' },
  { value: 'per_hour',    label: 'Per Hour' },
];

function EquipmentModal({ sport, orgId, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', total_qty: '', unit: 'piece', notes: '', rental_price: '', rental_price_type: 'per_session' });

  const fetchItems = () => {
    sportsApi.get('/sports/equipment', { params: { sport } })
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, [sport]);

  const resetForm = () => { setForm({ name: '', total_qty: '', unit: 'piece', notes: '', rental_price: '', rental_price_type: 'per_session' }); setEditingId(null); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Equipment name is required'); return; }
    if (!form.total_qty || parseInt(form.total_qty) < 1) { toast.error('Qty must be at least 1'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await sportsApi.put(`/sports/equipment/${editingId}`, {
          name: form.name, total_qty: parseInt(form.total_qty), unit: form.unit, notes: form.notes,
          rental_price: parseFloat(form.rental_price || 0), rental_price_type: form.rental_price_type,
        });
        toast.success('Equipment updated');
      } else {
        await sportsApi.post('/sports/equipment', {
          sport, name: form.name, total_qty: parseInt(form.total_qty), unit: form.unit, notes: form.notes,
          rental_price: parseFloat(form.rental_price || 0), rental_price_type: form.rental_price_type,
        });
        toast.success('Equipment added');
      }
      resetForm(); fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, total_qty: String(item.total_qty), unit: item.unit || 'piece', notes: item.notes || '', rental_price: String(item.rental_price || ''), rental_price_type: item.rental_price_type || 'per_session' });
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try {
      await sportsApi.delete(`/sports/equipment/${item.id}`);
      toast.success('Removed');
      fetchItems();
    } catch { toast.error('Failed to remove'); }
  };

  const inputStyle = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', width: '100%', outline: 'none', fontSize: 13, boxSizing: 'border-box' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}
      onClick={onClose}>
      <div className="rounded-xl w-full max-w-lg max-h-screen overflow-y-auto" style={{ background: '#141414', border: '1px solid #2A2A2A' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E', position: 'sticky', top: 0, background: '#141414', zIndex: 1 }}>
          <div>
            <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Equipment Rentals
            </p>
            <p className="text-xs mt-0.5 capitalize" style={{ color: '#737373' }}>{sport} — rental items offered to customers</p>
          </div>
          <button onClick={onClose} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Add/Edit form */}
          <div className="rounded-lg p-4" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
            <p className="text-xs uppercase tracking-wide mb-3" style={{ color: '#737373' }}>
              {editingId ? 'Edit Item' : 'Add Equipment Item'}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="col-span-2">
                <input data-testid="eq-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Item name (e.g. Racket, Ball)" style={inputStyle} />
              </div>
              <input data-testid="eq-qty-input" type="number" min="1" value={form.total_qty} onChange={e => setForm(f => ({ ...f, total_qty: e.target.value }))}
                placeholder="Total qty" style={inputStyle} />
              <select data-testid="eq-unit-select" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              {/* Rental Pricing */}
              <input data-testid="eq-price-input" type="number" min="0" step="0.01" value={form.rental_price}
                onChange={e => setForm(f => ({ ...f, rental_price: e.target.value }))}
                placeholder="Rental price (₱)" style={inputStyle} />
              <select data-testid="eq-price-type-select" value={form.rental_price_type}
                onChange={e => setForm(f => ({ ...f, rental_price_type: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                {PRICE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="col-span-2">
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Notes (optional)" style={inputStyle} />
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="eq-save-btn" onClick={handleSave} disabled={saving}
                className="flex-1 py-2 rounded-md text-sm font-bold uppercase transition-colors"
                style={{ background: '#E63946', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : editingId ? 'Update Item' : 'Add Item'}
              </button>
              {editingId && (
                <button onClick={resetForm} className="py-2 px-4 rounded-md text-sm" style={{ background: '#2A2A2A', color: '#A3A3A3' }}>
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Equipment list */}
          {loading ? (
            <div className="text-center py-6 text-sm" style={{ color: '#555' }}>Loading…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: '#555' }}>No equipment yet. Add your first item above.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide" style={{ color: '#737373' }}>Current Inventory</p>
              {items.map(item => (
                <div key={item.id} data-testid={`eq-item-${item.id}`}
                  className="flex items-center justify-between rounded-lg px-4 py-3"
                  style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#fff' }}>{item.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#737373' }}>
                      {item.total_qty} {item.unit}{item.total_qty !== 1 ? 's' : ''} total
                      {item.notes ? ` · ${item.notes}` : ''}
                    </p>
                    {(item.rental_price > 0) && (
                      <p className="text-xs mt-0.5 font-bold" style={{ color: '#E63946' }}>
                        ₱{item.rental_price.toLocaleString()} {item.rental_price_type === 'per_hour' ? '/ hr' : '/ session'}
                      </p>
                    )}
                    {(!item.rental_price || item.rental_price === 0) && (
                      <p className="text-xs mt-0.5" style={{ color: '#444' }}>Free add-on</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleEdit(item)} data-testid={`eq-edit-${item.id}`}
                      className="p-2 rounded-md transition-colors" style={{ background: '#2A2A2A', color: '#A3A3A3' }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item)} data-testid={`eq-delete-${item.id}`}
                      className="p-2 rounded-md transition-colors" style={{ background: '#2A1A1A', color: '#FF2D55' }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SportsMyCourtPage() {
  const navigate = useNavigate();
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockModal, setBlockModal] = useState(null); // court obj or null
  const [equipModal, setEquipModal] = useState(null); // sport string or null

  const fetchCourts = () => {
    sportsApi.get('/sports/courts')
      .then(r => setCourts(r.data || []))
      .catch(() => toast.error('Failed to load courts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCourts(); }, []);

  const handleToggle = async (court) => {
    try {
      await sportsApi.put(`/sports/courts/${court.id}`, { active: !court.active });
      toast.success(court.active ? 'Court deactivated' : 'Court activated');
      fetchCourts();
    } catch { toast.error('Failed to update court'); }
  };

  const handleDelete = async (court) => {
    if (!window.confirm(`Remove "${court.name}"? This will hide it from bookings.`)) return;
    try {
      await sportsApi.delete(`/sports/courts/${court.id}`);
      toast.success('Court removed');
      fetchCourts();
    } catch { toast.error('Failed to remove court'); }
  };

  const handleEdit = (court) => navigate(`/sports/courts/${court.id}/edit`);

  const handleDuplicate = async (court) => {
    if (!window.confirm(`Duplicate "${court.name}"? A copy will be created as inactive so you can edit it before going live.`)) return;
    try {
      const res = await sportsApi.post(`/sports/courts/${court.id}/duplicate`);
      toast.success(`Duplicated as "${res.data.name}" (inactive — edit before activating)`);
      fetchCourts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplication failed'); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Manage</p>
            <h1 className="text-4xl uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>My Courts</h1>
          </div>
          <button data-testid="sports-add-court-btn"
            onClick={() => navigate('/sports/courts/new')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold uppercase tracking-wide transition-all"
            style={{ background: '#E63946', color: '#fff', boxShadow: '0 0 18px rgba(230,57,70,0.3)' }}>
            <Plus className="h-4 w-4" /> Add Court
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-xl h-52 animate-pulse" style={{ background: '#141414' }} />
            ))}
          </div>
        ) : courts.length === 0 ? (
          <div className="text-center py-20 rounded-xl" style={{ border: '1px dashed #2A2A2A' }}>
            <p className="text-4xl mb-4">🏟</p>
            <h3 className="text-xl uppercase font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>No Courts Yet</h3>
            <p className="text-sm mb-6" style={{ color: '#737373' }}>Add your first court to start accepting bookings.</p>
            <button data-testid="sports-empty-add-court-btn"
              onClick={() => navigate('/sports/courts/new')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wide"
              style={{ background: '#E63946', color: '#fff' }}>
              <Plus className="h-4 w-4" /> Open a Court
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {courts.map(c => (
                <CourtCard key={c.id} court={c}
                  onEdit={handleEdit} onToggle={handleToggle} onDelete={handleDelete}
                  onDuplicate={handleDuplicate} onBlockDates={setBlockModal} />
              ))}
            </div>

            {/* Equipment Rentals Section — grouped by sport */}
            {(() => {
              const sports = [...new Set(courts.map(c => c.sport))];
              return (
                <div className="mt-14">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Add-ons</p>
                      <h2 className="text-3xl uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>Equipment Rentals</h2>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sports.map(sport => {
                      const sportColor = SPORT_COLORS[sport] || SPORT_COLORS.other;
                      return (
                        <div key={sport} data-testid={`sports-equipment-card-${sport}`}
                          className="rounded-xl p-5 transition-all"
                          style={{ background: '#141414', border: '1px solid #2A2A2A' }}>
                          <div className="flex items-center gap-3 mb-4">
                            <span className="w-9 h-9 flex items-center justify-center rounded-lg"
                              style={{ background: sportColor + '18' }}>
                              <SportIcon sport={sport} size={20} color={sportColor} />
                            </span>
                            <div>
                              <p className="text-sm font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{sport}</p>
                              <p className="text-xs" style={{ color: '#555' }}>Shared across all {sport} courts</p>
                            </div>
                          </div>
                          <button data-testid={`sports-manage-equipment-${sport}`}
                            onClick={() => setEquipModal(sport)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold uppercase tracking-wide transition-all"
                            style={{ background: sportColor + '18', border: `1px solid ${sportColor}30`, color: sportColor }}
                            onMouseEnter={e => { e.currentTarget.style.background = sportColor + '28'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = sportColor + '18'; }}>
                            <Package className="h-4 w-4" /> Manage Equipment
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </main>
      {blockModal && (
        <BlockDatesModal court={blockModal} onClose={() => { setBlockModal(null); fetchCourts(); }} onRefresh={fetchCourts} />
      )}
      {equipModal && (
        <EquipmentModal sport={equipModal} onClose={() => setEquipModal(null)} />
      )}
    </div>
  );
}
